/**
 * useFeatureFlag - React hook for feature flag checks
 *
 * Usage:
 * ```typescript
 * const showNewDashboard = useFeatureFlag('NEW_DASHBOARD');
 * if (showNewDashboard) {
 *   return <DashboardV2 />;
 * }
 * return <DashboardLegacy />;
 * ```
 */

import { useEffect, useState } from 'react';
import { isFeatureEnabled, type FeatureFlagKey } from '@/lib/feature-flags';

export function useFeatureFlag(flag: FeatureFlagKey): boolean {
  const [isEnabled, setIsEnabled] = useState(() => isFeatureEnabled(flag));

  useEffect(() => {
    // Listen for localStorage changes (for testing/admin toggling)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === `feature.${flag}`) {
        setIsEnabled(isFeatureEnabled(flag));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [flag]);

  return isEnabled;
}

/**
 * useAllFeatureFlags - Get all feature flag states
 *
 * Useful for admin panels or debugging dashboards
 */
export function useAllFeatureFlags(): Record<FeatureFlagKey, boolean> {
  const [flags, setFlags] = useState(() => {
    // Initialize with current states
    const initial = {} as Record<FeatureFlagKey, boolean>;
    const keys: FeatureFlagKey[] = [
      'NEW_NAVIGATION',
      'NEW_DASHBOARD',
      'PIPELINE_BOARD',
      'COMMAND_PALETTE',
      'NEW_AUDIT_WIZARD',
      'RESULTS_DRAWER',
      'MINIMAL_RESULT_SCREEN',
    ];
    keys.forEach(key => {
      initial[key] = isFeatureEnabled(key);
    });
    return initial;
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const updated = {} as Record<FeatureFlagKey, boolean>;
      const keys: FeatureFlagKey[] = [
        'NEW_NAVIGATION',
        'NEW_DASHBOARD',
        'PIPELINE_BOARD',
        'COMMAND_PALETTE',
        'NEW_AUDIT_WIZARD',
        'RESULTS_DRAWER',
        'MINIMAL_RESULT_SCREEN',
      ];
      keys.forEach(key => {
        updated[key] = isFeatureEnabled(key);
      });
      setFlags(updated);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return flags;
}
